import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Share2, Trash2, Link2, Lock, History, ChevronDown, ChevronRight } from 'lucide-react';
import Spinner from '../../components/ui/Spinner';
import Button from '../../components/ui/Button';
import { formatDate } from '../../utils/format';
import { getErrorMessage } from '../../lib/apiClient';
import { fileKeys, getShareEvents, listShares, revokeShare } from '../files/filesApi';
import { useTenant } from '../../hooks/useTenant';
import { ACTIONS } from '../tenancy/actions';

function ShareEventsPanel({ shareId }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: fileKeys.shareEvents(shareId),
    queryFn: () => getShareEvents(shareId, { limit: 50 }),
  });
  if (isLoading) return <div className="px-4 py-3"><Spinner /></div>;
  // ACTIVITY_READ is required server-side; members without it get a 403.
  if (isError)
    return <p className="px-4 py-3 text-xs text-slate-400">Access history isn’t available to you.</p>;
  const events = data?.data ?? [];
  if (events.length === 0)
    return <p className="px-4 py-3 text-xs text-slate-400">No access recorded yet.</p>;
  return (
    <ul className="divide-y divide-slate-100 bg-slate-50/60">
      {events.map((e) => (
        <li key={e.id} className="flex items-center gap-3 px-6 py-2 text-xs">
          <span
            className={`w-16 font-medium ${e.event === 'denied' ? 'text-red-600' : 'text-slate-600'}`}
          >
            {e.event}
          </span>
          <span className="flex-1 truncate text-slate-400">
            {e.ip ?? '—'}
            {e.deniedReason ? ` · ${e.deniedReason}` : ''}
          </span>
          <span className="text-slate-400">{formatDate(e.createdAt)}</span>
        </li>
      ))}
    </ul>
  );
}

export default function SharesPage() {
  const { allows } = useTenant();
  const canRevoke = allows(ACTIONS.SHARE_REVOKE);
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['shares', {}],
    queryFn: () => listShares({ limit: 100 }),
  });

  const revokeMutation = useMutation({
    mutationFn: revokeShare,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      toast.success('Link revoked');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not revoke')),
  });

  const shares = data?.data ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex items-center gap-2">
        <Share2 className="h-5 w-5 text-slate-500" />
        <h1 className="text-lg font-semibold text-slate-800">Shared links</h1>
      </div>
      <p className="text-sm text-slate-500">
        Links are shown once at creation (we only store a hash). Revoke any you no longer need.
      </p>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
        {isLoading ? (
          <div className="py-16">
            <Spinner />
          </div>
        ) : shares.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">
            You haven&apos;t created any share links yet.
          </div>
        ) : (
          shares.map((share) => {
            const revoked = Boolean(share.revokedAt);
            const expired = share.expiresAt && new Date(share.expiresAt) <= new Date();
            const uses = `${share.usedCount}${share.maxUses ? ` / ${share.maxUses}` : ''}`;
            const expanded = expandedId === share.id;
            return (
              <div key={share.id} className="border-b border-slate-100">
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    onClick={() => setExpandedId(expanded ? null : share.id)}
                    className="text-slate-400 hover:text-brand-600"
                    title="Access history"
                  >
                    {expanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  <Link2 className={`h-5 w-5 ${revoked ? 'text-slate-300' : 'text-brand-500'}`} />
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-1.5 truncate text-sm font-medium text-slate-700">
                      {share.resourceName ?? (
                        <span className="italic text-slate-400">
                          Deleted {share.resourceType}
                        </span>
                      )}
                      {share.hasPassword && <Lock className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                      {revoked && (
                        <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-600">
                          revoked
                        </span>
                      )}
                      {!revoked && expired && (
                        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700">
                          expired
                        </span>
                      )}
                    </p>
                    <p className="truncate text-xs text-slate-400">
                      {share.accessMode}
                      {share.allowDownload ? '' : ' · no download'} · ({share.linkType}) · Created{' '}
                      {formatDate(share.createdAt)} · {uses} uses
                      {share.expiresAt ? ` · expires ${formatDate(share.expiresAt)}` : ''}
                    </p>
                  </div>
                  {canRevoke && !revoked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => revokeMutation.mutate(share.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
                {expanded && (
                  <div className="border-t border-slate-100">
                    <p className="flex items-center gap-1.5 px-6 pt-2 text-xs font-medium uppercase tracking-wide text-slate-400">
                      <History className="h-3.5 w-3.5" /> Access history
                    </p>
                    <ShareEventsPanel shareId={share.id} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
